import { Button, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import { reimportFile } from "../../../../api/endpoints";
import { IconAttachment } from "../../../../utils/icons";
import { PanelFooter } from "./PanelFooter";
import { DotLottleLoader } from "../../../../components/Loader";

const TRANSCRIPT_REQUEST_COOLDOWN_MS = 5 * 60 * 1000;
const TRANSCRIPT_REQUEST_KEY = "requestedTranscripts";

const readExpiryMap = (): Record<string, number> => {
    try {
        const raw = localStorage.getItem(TRANSCRIPT_REQUEST_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        // legacy shape was an array of ids; discard it.
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        return parsed as Record<string, number>;
    } catch {
        return {};
    }
};

const getCooldownEnd = (id: number): number | null => {
    const map = readExpiryMap();
    const exp = map[String(id)];
    return typeof exp === "number" && exp > Date.now() ? exp : null;
};

const setCooldown = (id: number, expiresAt: number) => {
    const map = readExpiryMap();
    const now = Date.now();
    for (const k of Object.keys(map)) {
        if (map[k] <= now) delete map[k];
    }
    map[String(id)] = expiresAt;
    try {
        localStorage.setItem(TRANSCRIPT_REQUEST_KEY, JSON.stringify(map));
    } catch {
        // localStorage unavailable; cooldown is best-effort.
    }
};

interface Props {
    transcript: string | null;
    panelHeight?: number;
    videoId: number;
    hasTranscript: boolean;
    transcriptStatus: string;
    // Re-import (which runs transcription) locates the file through its
    // project's S3 folder, so project-less files can't be requested.
    isOnProject: boolean;
    onTranscriptRequested: () => void;
    onRegenerate?: () => void;
    isRegenerating?: boolean;
}

export function TranscriptPanel({
    transcript,
    panelHeight,
    videoId,
    hasTranscript,
    transcriptStatus,
    isOnProject,
    onTranscriptRequested,
    onRegenerate,
    isRegenerating
}: Props) {
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [isRequesting, setIsRequesting] = useState<boolean>(false);
    const [cooldownEnd, setCooldownEnd] = useState<number | null>(() => getCooldownEnd(videoId));

    useEffect(() => {
        setCooldownEnd(getCooldownEnd(videoId));
    }, [videoId]);

    useEffect(() => {
        if (!cooldownEnd) return;
        const ms = cooldownEnd - Date.now();
        if (ms <= 0) {
            setCooldownEnd(null);
            return;
        }
        const t = setTimeout(() => setCooldownEnd(null), ms);
        return () => clearTimeout(t);
    }, [cooldownEnd]);

    const onCopyBtnClick = () => {
        if (transcript) {
            navigator.clipboard.writeText(transcript);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const onRequestTranscript = async () => {
        setIsRequesting(true);
        try {
            await axiosInstant.get(reimportFile(videoId));
            const expiresAt = Date.now() + TRANSCRIPT_REQUEST_COOLDOWN_MS;
            setCooldown(videoId, expiresAt);
            setCooldownEnd(expiresAt);
            toast.success("Transcript requested. The file will refresh once processing completes.");
            onTranscriptRequested();
        } catch (err: any) {
            console.error("Failed to request transcript:", err);
            const status = err?.response?.status;
            // proTeamControl sends the error code as a bare JSON string body.
            const data = err?.response?.data;
            const code = typeof data === "string" ? data : data?.error;
            if (status === 404 && code === "ProjectFolderNotFoundInS3") {
                toast.error("This video's project folder wasn't found in storage — it can't be re-imported.");
            } else if (status === 404) {
                toast.error("File no longer exists.");
            } else if (status === 400 && code === "FileNotOnProject") {
                toast.error("This video isn't on a project, so a transcript can't be requested.");
            } else if (status === 400 && code === "FileMissingKey") {
                toast.error("File hasn't been delivered yet — nothing to transcribe.");
            } else if (status >= 500) {
                toast.error("Transcript request failed, please try again.");
            } else {
                toast.error(err?.response?.data?.message || "Failed to request transcript.");
            }
        } finally {
            setIsRequesting(false);
        }
    };

    // No transcript available
    if (!hasTranscript && !transcript) {
        const isInCooldown = cooldownEnd !== null;
        const minsRemaining = isInCooldown
            ? Math.max(1, Math.ceil((cooldownEnd! - Date.now()) / 60000))
            : 0;

        return (
            <div
                className="flex flex-col gap-4 justify-center items-center h-full py-12"
                style={panelHeight ? { height: panelHeight - 240 } : undefined}
            >
                <Typography color="textSecondary">No transcript available for this video.</Typography>
                {!isOnProject ? (
                    <Typography variant="body2" color="textSecondary" className="text-center max-w-sm">
                        Transcripts can only be requested for videos on a project.
                    </Typography>
                ) : (
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onRequestTranscript}
                            disabled={isRequesting || transcriptStatus === "pending" || isInCooldown}
                            startIcon={isRequesting ? <DotLottleLoader fill={false} sx={{ width: 20, height: 20 }} /> : undefined}
                            sx={{ textTransform: "capitalize" }}
                        >
                            {isRequesting
                                ? "Requesting..."
                                : transcriptStatus === "pending"
                                ? "Transcript Requested"
                                : isInCooldown
                                ? `Try again in ${minsRemaining} min`
                                : "Request Transcript"}
                        </Button>
                        {transcriptStatus === "pending" && (
                            <Typography variant="body2" color="textSecondary">
                                Transcript has been requested. Please check back later.
                            </Typography>
                        )}
                        {isInCooldown && transcriptStatus !== "pending" && (
                            <Typography variant="body2" color="textSecondary">
                                Transcript already requested — please wait before requesting again.
                            </Typography>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div
            className="flex flex-col gap-2 justify-between h-full"
            style={panelHeight ? { height: panelHeight - 240 } : undefined}
        >
            <div className="py-6 flex flex-col w-full gap-4 overflow-auto flex-1">
                <Typography variant="subtitle2" className="text-gray-600">
                    Video Transcript
                </Typography>

                <div className="p-4 bg-surface-2 rounded-md overflow-auto max-h-[400px]">
                    <p
                        style={{
                            wordWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            fontFamily: "CircularStd",
                            fontSize: "14px",
                            lineHeight: "1.6"
                        }}
                    >
                        {transcript}
                    </p>
                </div>
            </div>
            <PanelFooter>
                {onRegenerate && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={onRegenerate}
                        disabled={isRegenerating}
                        startIcon={isRegenerating ? <DotLottleLoader fill={false} sx={{ width: 20, height: 20 }} /> : undefined}
                        sx={{ textTransform: "capitalize" }}
                    >
                        {isRegenerating ? "Regenerating..." : "Regenerate"}
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onCopyBtnClick}
                    endIcon={<IconAttachment stroke="#fff" />}
                    sx={{ textTransform: "capitalize" }}
                >
                    {isCopied ? "Copied!" : "Copy Transcript"}
                </Button>
            </PanelFooter>
        </div>
    );
}
